// Resume reconciler — heals the iOS Safari background→resume modal lockup.
//
// PROBLEM: on iOS Safari, backgrounding the tab while a native <dialog> is open
// (modal) and then resuming can leave the dialog's backdrop / top-layer in a
// stale state that swallows all pointer + focus events — a full-page lockup the
// player can only escape by killing the tab. The dialog still reports .open and
// is still in the DOM; only its top-layer membership is corrupted.
//
// CURE: on a resume signal, for every currently-open dialog, close() it and
// immediately showModal() it again. That re-promotes it into the top layer with
// a fresh backdrop, clearing the stale state. Argless close() preserves
// returnValue, so a re-asserted onboarding/endgame dialog keeps its pending
// choice. The dialog's real 'close' listeners must NOT run teardown during this
// cycle — they bail via the modalReassert flag set here.
//
// SCOPE: this module imports only the modalReassert leaf primitive (+ DOM
// types). It deliberately does NOT import warmupWelcome or modal — it operates
// generically over `dialog[open]` in the document, so it stays decoupled from
// which modules created the dialogs.

import { isReasserting, markReasserting, unmarkReasserting } from './modalReassert'

// The two resume signals we route through, modeled as a discriminated union so
// the truth table below is exhaustive + unit-testable in node without a DOM.
export type ResumeSignal =
  | { readonly kind: 'pageshow'; readonly persisted: boolean }
  | { readonly kind: 'visibilitychange'; readonly visibilityState: DocumentVisibilityState }

// PURE truth table. The single decision point for "did we just resume from a
// state that could have corrupted a modal's top layer?":
//   - pageshow:          reconcile iff restored from bfcache (persisted === true)
//   - visibilitychange:  reconcile iff the document just became visible
// Deliberately NOT triggered on window 'focus' — focus fires far more often
// (tab switches within the foreground, devtools, etc.) and would re-assert
// modals needlessly; visibilitychange + bfcache pageshow are the precise pair.
export function shouldReconcileOnResume(signal: ResumeSignal): boolean {
  switch (signal.kind) {
    case 'pageshow':
      return signal.persisted === true
    case 'visibilitychange':
      return signal.visibilityState === 'visible'
  }
}

// Re-assert a single dialog's modality: close() then showModal(). Skips a
// dialog that isn't actually open + connected (F6/F7) — re-asserting a closed
// or detached dialog would either no-op the close() then throw on showModal()
// of an orphan, or worse pop a dialog the player already dismissed.
//
// Depends on the spec-synchronous 'close' event: HTMLDialogElement.close()
// dispatches its 'close' event synchronously (per the HTML spec's "close the
// dialog" steps), so the REASSERT_ATTR we set before close() is guaranteed to
// be readable by the dialog's 'close' listeners while it is set, and we can
// safely clear it in the finally after showModal(). The argless close()
// preserves the dialog's returnValue.
export function reassertModality(dialog: HTMLDialogElement): void {
  if (!dialog.open || !dialog.isConnected) return
  markReasserting(dialog)
  try {
    dialog.close()
    dialog.showModal()
  } finally {
    unmarkReasserting(dialog)
  }
}

// Re-assert every open dialog in `doc`. Materializes the matches into an array
// FIRST (a live NodeList could shift as close()/showModal() mutate the top
// layer), then iterates with each dialog wrapped in its OWN try/catch and its
// OWN .open && .isConnected recheck. This is the load-bearing defensive detail:
// if one dialog's re-assert throws (a teardown race detached it between the
// querySelectorAll snapshot and our turn, a hostile showModal, etc.), the
// remaining dialogs are still reconciled rather than stranded in the locked
// state we're trying to heal.
export function reconcileOpenDialogs(doc: Document): void {
  const dialogs = Array.from(doc.querySelectorAll<HTMLDialogElement>('dialog[open]'))
  for (const dialog of dialogs) {
    // Per-dialog recheck: an earlier dialog's re-assert (or a concurrent
    // teardown) may have closed/detached this one since the snapshot.
    if (!dialog.open || !dialog.isConnected) continue
    if (isReasserting(dialog)) continue
    try {
      reassertModality(dialog)
    } catch {
      // Swallow — one dialog failing must not strand the rest. The dialog is
      // no worse off than the locked state we entered with.
    }
  }
}

export interface InstallResumeReconcilerOptions {
  // Fired after a reconcile pass completes. The orchestrator uses this to reset
  // the double-tap-zoom latch (a long background gap can leave a stale
  // lastTouchEnd timestamp that would suppress the player's first tap on
  // resume). Optional so the reconciler stays usable without the hook.
  readonly onResume?: () => void
}

// Install the resume listeners. Routes window 'pageshow' + document
// 'visibilitychange' through shouldReconcileOnResume → reconcileOpenDialogs →
// onResume. Returns a disposer that removes both listeners (handy for tests /
// teardown; main() installs once and never disposes).
export function installResumeReconciler(
  opts: InstallResumeReconcilerOptions = {},
): () => void {
  const onPageShow = (event: PageTransitionEvent): void => {
    if (!shouldReconcileOnResume({ kind: 'pageshow', persisted: event.persisted })) return
    reconcileOpenDialogs(document)
    opts.onResume?.()
  }
  const onVisibilityChange = (): void => {
    if (
      !shouldReconcileOnResume({
        kind: 'visibilitychange',
        visibilityState: document.visibilityState,
      })
    ) {
      return
    }
    reconcileOpenDialogs(document)
    opts.onResume?.()
  }

  window.addEventListener('pageshow', onPageShow)
  document.addEventListener('visibilitychange', onVisibilityChange)

  return (): void => {
    window.removeEventListener('pageshow', onPageShow)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
